import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, IconButton, Chip, Tooltip, Button, Collapse,
    CircularProgress, useTheme, Avatar, TextField, FormControl,
    Select, MenuItem, SelectChangeEvent, List, ListItem,
    ListItemAvatar, ListItemText, Tab, Tabs, Divider, Link,
    Dialog, DialogContent, DialogActions, Skeleton
} from '@mui/material';
import {
    Close as CloseIcon, ContentCopy as CopyIcon, OpenInNew as OpenInNewIcon,
    ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
    Schedule as ScheduleIcon, Send as SendIcon, AttachFile as AttachFileIcon,
    Search as SearchIcon, Shield as ShieldIcon, Warning as WarningIcon,
    BugReport as BugReportIcon, NetworkCheck as NetworkIcon,
    Layers as LayersIcon, Lightbulb as LightbulbIcon,
    Assessment as AssessmentIcon, Code as CodeIcon,
    CheckCircle as CheckCircleIcon, RadioButtonUnchecked as UncheckedIcon,
    EditOutlined as EditOutlinedIcon,
    Download as DownloadIcon, DeleteOutline as DeleteIcon,
    ArticleOutlined as ArticleOutlinedIcon,
    AutoAwesome as AISuggestionsIcon,
    ThumbUp as AcceptIcon,
    ThumbDown as RejectIcon,
    EscalatorWarning as EscalateIcon,
    Flag as FlagIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import { InputLabel, DialogTitle } from '@mui/material';
import { fetchAssignableUsers } from '../store/slices/usersSlice';
import * as TicketMappers from '../theme/mappers/ticketMappers';
import { colorUtils } from '../theme';
import { IOCType, TimelineEventType, VendorSeverity } from "../types";
import {
    getStatusMapping, getRiskScoreStyles, getSeverityStyles,
    getTimelineIcon, getIOCIcon, getStatusChipSx, getSeverityChipSx, getPriorityColor
} from '../theme/mappers/ticketMappers';
import {
    ExtendedTicket, IOC, TimelineEvent, DuplicateAlert,
    RootState, TicketStatus, User, MitreMapping, ThreatIntelEnrichment,
    RecommendedAction, IncidentMemory,
} from '../types';
import { CuratorService } from '../services/curatorService';
import {
    clearSelectedTicket, fetchTicketComments, fetchTicketAttachments,
    addTicketComment, addTicketAttachment, deleteTicketAttachment, updateTicket,
    resolveTicket, escalateTicket,
} from '../store/slices/ticketsSlice';
import { fetchAlertById } from '../store/slices/alertsSlice';
import { IntegrationAlert } from '../types';
import { formatDate, getRelativeTimeString } from '../utils/dateUtils';
import api from '../utils/axiosConfig';
import { AlertsService } from '../services/alertsService';
import { PlaybooksService } from '../services/playbooksService';
import { TicketsService } from '../services/ticketsService';
import { AISuggestion } from '../types';
import { PlaybookSession } from '../types';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import EditTicketModal from './EditTicketModal';
import Nis2ReportModal from './nis2/Nis2ReportModal';
import DDoSPlaybookPanel from './DDoSPlaybookPanel';
import RecommendationCard from './playbook/RecommendationCard';

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const CollapsibleSection: React.FC<{
    title: string; icon: React.ReactNode; badge?: string | number;
    children: React.ReactNode; defaultExpanded?: boolean; forceCollapsed?: boolean;
    onToggle?: (expanded: boolean) => void;
}> = ({ title, icon, badge, children, defaultExpanded = true, forceCollapsed = false, onToggle }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const theme = useTheme();

    // When forceCollapsed flips to true, collapse the section
    useEffect(() => {
        if (forceCollapsed) setExpanded(false);
    }, [forceCollapsed]);

    const handleToggle = () => {
        const next = !expanded;
        setExpanded(next);
        onToggle?.(next);
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Box
                onClick={handleToggle}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 1,
                    cursor: 'pointer',

                    transition: 'border-color 0.2s',
                    '&:hover .section-icon': { color: theme.palette.ui.iconActive },
                }}
            >
                <Box
                    className="section-icon"
                    sx={{
                        color: expanded ? theme.palette.ui.iconActive : theme.palette.ui.iconDefault,
                        display: 'flex',
                        transition: 'color 0.2s',
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="subtitle2" sx={{ flex: 1, color: theme.palette.text.secondary, fontWeight: 600 }}>
                    {title}
                </Typography>
                {badge !== undefined && (
                    <Typography variant="caption" sx={{
                        color: theme.palette.text.tertiary,
                        fontWeight: 600,
                        mr: 1,
                        fontFamily: '"JetBrains Mono", monospace',
                    }}>
                        {badge}
                    </Typography>
                )}
                <Box sx={{ color: theme.palette.text.tertiary }}>
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ pt: 1.5, pl: 4, pr: 1 }}>{children}</Box>
            </Collapse>
        </Box>
    );
};

// Read-only: shows the senior analyst's verdict once recorded. Submitting a
// verdict is a senior-only action done from the Review page, not from here.
const VerdictPanel: React.FC<{
    session: PlaybookSession;
}> = ({ session }) => {
    const theme = useTheme();

    const verdictColor = (v: string) =>
        v === 'correct'   ? theme.palette.severity.low :
        v === 'incorrect' ? theme.palette.severity.critical :
        theme.palette.warning.main;

    // Parse structured correction reason from notes ([REASON:xxx] prefix)
    const parseNotes = (raw: string | null) => {
        if (!raw) return { reason: null, notes: null };
        if (raw.startsWith('[REASON:')) {
            const end = raw.indexOf(']');
            if (end !== -1) {
                const key = raw.slice(8, end);
                const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                return { reason: label, notes: raw.slice(end + 1).trim() || null };
            }
        }
        return { reason: null, notes: raw };
    };

    if (!session.analyst_verdict) return null;

    const color = verdictColor(session.analyst_verdict);
    const { reason, notes: parsedNotes } = parseNotes(session.analyst_verdict_notes);
    return (
        <Box sx={{
            p: 1.5, borderRadius: 1.5,
            backgroundColor: colorUtils.withOpacity(color, 0.06),
            border: `1px solid ${colorUtils.withOpacity(color, 0.2)}`,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip
                    label={session.analyst_verdict.toUpperCase()}
                    size="small"
                    sx={{
                        height: 20, fontSize: '0.65rem', fontWeight: 700,
                        color, backgroundColor: colorUtils.withOpacity(color, 0.12),
                        border: `1px solid ${colorUtils.withOpacity(color, 0.3)}`,
                    }}
                />
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', fontWeight: 600 }}>
                    Reviewed by {session.analyst_verdict_by_name ?? 'Senior analyst'}
                </Typography>
                {session.analyst_verdict_at && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem', ml: 'auto' }}>
                        {getRelativeTimeString(session.analyst_verdict_at)}
                    </Typography>
                )}
            </Box>
            {reason && (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', display: 'block' }}>
                    Issue: {reason}
                </Typography>
            )}
            {parsedNotes && (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', display: 'block', mt: 0.25 }}>
                    {parsedNotes}
                </Typography>
            )}
        </Box>
    );
};

const EntityRow: React.FC<{
    label: string; icon: React.ReactNode;
    primaryValue: string;
    details: { label: string; value: string }[];
    statusBadge?: { label: string; color: string };
}> = ({ label, icon, primaryValue, details, statusBadge }) => {
    const theme = useTheme();
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1,
            py: 1,
            px: 1.5,
            borderRadius: 1.5,
            backgroundColor: theme.palette.background.tertiary,
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: theme.palette.interactive.hover },
        }}>
            <Box sx={{ color: theme.palette.ui.iconDefault, display: 'flex' }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{
                    color: theme.palette.text.tertiary,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    display: 'block',
                    mb: 0.25,
                }}>
                    {label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                    }}>
                        {primaryValue}
                    </Typography>
                    {details.map((d, i) => (
                        <Typography key={i} variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                        }}>
                            {d.label}: <Box component="span" sx={{ color: theme.palette.text.secondary }}>{d.value}</Box>
                        </Typography>
                    ))}
                </Box>
            </Box>
            {statusBadge && (
                <Chip
                    label={statusBadge.label}
                    size="small"
                    sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        backgroundColor: colorUtils.withOpacity(statusBadge.color, 0.08),
                        color: statusBadge.color,
                        border: `1px solid ${colorUtils.withOpacity(statusBadge.color, 0.19)}`,
                    }}
                />
            )}
        </Box>
    );
};

const CopyIconButton: React.FC<{ value: string }> = ({ value }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy'} arrow>
            <IconButton size="small" onClick={handleCopy} sx={{ color: theme.palette.ui.iconDefault }}>
                {copied
                    ? <CheckCircleIcon sx={{ fontSize: 14, color: theme.palette.severity.low }} />
                    : <CopyIcon sx={{ fontSize: 14 }} />
                }
            </IconButton>
        </Tooltip>
    );
};

const CopyableValue: React.FC<{ value: string; label?: string }> = ({ value, label }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            '&:hover .copy-btn': { opacity: 1 }
        }}>
            <Typography variant="caption" sx={{
                fontFamily: '"JetBrains Mono", monospace',
                color: theme.palette.text.secondary,
            }}>
                {label || value}
            </Typography>
            <IconButton
                className="copy-btn"
                size="small"
                onClick={handleCopy}
                sx={{ opacity: 0, transition: 'opacity 0.2s', p: 0.25 }}
            >
                {copied
                    ? <CheckCircleIcon sx={{ fontSize: 12, color: theme.palette.severity.low }} />
                    : <CopyIcon sx={{ fontSize: 12, color: theme.palette.ui.iconDefault }} />
                }
            </IconButton>
        </Box>
    );
};

const AlertIdChip: React.FC<{ alertId: string; integrationType: string }> = ({ alertId, integrationType }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(alertId);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy alert ID'} arrow>
            <Chip
                label={`${integrationType} — ${alertId.slice(0, 8)}...`}
                size="small"
                onClick={handleCopy}
                icon={copied
                    ? <CheckCircleIcon sx={{ fontSize: 12, color: `${theme.palette.severity.low} !important` }} />
                    : <CopyIcon sx={{ fontSize: 12, color: `${theme.palette.text.tertiary} !important` }} />
                }
                sx={{
                    height: 22,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    fontFamily: '"JetBrains Mono", monospace',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    color: theme.palette.text.secondary,
                    border: `1px solid ${theme.palette.border.main}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: colorUtils.withOpacity(theme.palette.text.primary, 0.04),
                        borderColor: theme.palette.border.strong,
                        color: theme.palette.text.primary,
                    },
                }}
            />
        </Tooltip>
    );
};

const TicketIdChip: React.FC<{ ticketId: string }> = ({ ticketId }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(ticketId);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy ticket ID'} arrow>
            <Chip
                label={ticketId}
                size="small"
                onClick={handleCopy}
                icon={copied
                    ? <CheckCircleIcon sx={{ fontSize: 11, color: `${theme.palette.severity.low} !important` }} />
                    : <CopyIcon sx={{ fontSize: 11, color: `${theme.palette.text.tertiary} !important` }} />
                }
                sx={{
                    height: 22,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    fontFamily: '"JetBrains Mono", monospace',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    color: theme.palette.text.secondary,
                    border: `1px solid ${theme.palette.border.main}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: colorUtils.withOpacity(theme.palette.text.primary, 0.04),
                        borderColor: theme.palette.border.strong,
                        color: theme.palette.text.primary,
                    },
                }}
            />
        </Tooltip>
    );
};

const AnalystTextField: React.FC<{ text: string }> = ({ text }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);
    return (
        <Box>
            <Typography
                variant="body2"
                sx={{
                    fontSize: '0.82rem',
                    lineHeight: 1.6,
                    color: theme.palette.text.secondary,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: expanded ? 'unset' : 2,
                    overflow: 'hidden',
                    whiteSpace: 'pre-wrap',
                }}
            >
                {text}
            </Typography>
            <Box
                onClick={() => setExpanded(e => !e)}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    mt: 0.5,
                    cursor: 'pointer',
                    color: theme.palette.text.tertiary,
                    '&:hover': { color: theme.palette.text.secondary },
                }}
            >
                <Typography sx={{ fontSize: '0.7rem' }}>
                    {expanded ? 'Show less' : 'Show more'}
                </Typography>
                {expanded
                    ? <ExpandLessIcon sx={{ fontSize: 14 }} />
                    : <ExpandMoreIcon sx={{ fontSize: 14 }} />
                }
            </Box>
        </Box>
    );
};

const AnalystAnalysisSection: React.FC<{
    description: string;
    clientRecommendations: string | null;
    resolutionSummary: string | null;
    aiSummary?: string | null;
    ticketId: string;
    onAiRefresh: () => void;
}> = ({ description, aiSummary, ticketId, onAiRefresh }) => {
    const theme = useTheme();
    const [generating, setGenerating] = useState(false);

    const handleRegenerate = async () => {
        setGenerating(true);
        try {
            await PlaybooksService.generateForTicket(ticketId);
            onAiRefresh();
        } catch {
            // silent — backend logs the error
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Box sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.border.light}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <EditOutlinedIcon sx={{ fontSize: 13, color: theme.palette.text.tertiary }} />
                    <Typography variant="caption" sx={{
                        color: theme.palette.text.tertiary,
                        fontSize: '0.65rem',
                        letterSpacing: '0.08em',
                        fontWeight: 600,
                    }}>
                        ANALYSIS
                    </Typography>
                </Box>
                <Tooltip title={aiSummary ? 'Regenerate AI summary' : 'Generate AI summary'}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleRegenerate}
                            disabled={generating}
                            sx={{ p: 0.5, color: theme.palette.text.tertiary }}
                        >
                            {generating
                                ? <CircularProgress size={12} />
                                : <LightbulbIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            {description && <AnalystTextField text={description} />}

            {aiSummary && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <LightbulbIcon sx={{ fontSize: 13, color: theme.palette.warning.main }} />
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                            letterSpacing: '0.08em',
                            fontWeight: 600,
                        }}>
                            AI INCIDENT SUMMARY
                        </Typography>
                    </Box>
                    <AnalystTextField text={aiSummary} />
                </Box>
            )}

        </Box>
    );
};

const MetricBox: React.FC<{
    label: string;
    value: string | number;
    color?: string;
    subLabel?: string;
    noTruncate?: boolean;
}> = ({ label, value, color, subLabel, noTruncate }) => {
    const theme = useTheme();
    const strValue = String(value);
    return (
        <Box sx={{ minWidth: 80, maxWidth: noTruncate ? 'none' : 140, overflow: 'hidden' }}>
            <Typography variant="caption" sx={{
                color: theme.palette.text.tertiary,
                fontSize: '0.65rem',
                letterSpacing: '0.08em',
                display: 'block',
                mb: 0.5,
                whiteSpace: 'nowrap',
            }}>
                {label}
            </Typography>
            <Tooltip title={strValue} arrow disableHoverListener={noTruncate || strValue.length <= 20}>
                <Typography sx={{
                    color: color || theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    overflow: noTruncate ? 'visible' : 'hidden',
                    textOverflow: noTruncate ? 'unset' : 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {value}
                </Typography>
            </Tooltip>
            {subLabel && (
                <Typography variant="caption" sx={{
                    color: theme.palette.text.tertiary,
                    fontSize: '0.65rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                }}>
                    {subLabel}
                </Typography>
            )}
        </Box>
    );
};

// ============================================================================
// ALERT EXPLORER SUB-COMPONENT
// ============================================================================

const AlertExplorer: React.FC<{
    integrationAlerts: IntegrationAlert[];
}> = ({ integrationAlerts }) => {
    const theme = useTheme();
    const [selectedAlertId, setSelectedAlertId] = useState<string | null>(integrationAlerts[0]?.id || null);
    const [alertCache, setAlertCache] = useState<Record<string, any>>({});
    const [isLoading, setIsLoading] = useState(false);

    // Build chip labels: vendor name, with index suffix if duplicates exist
    const chipLabels = React.useMemo(() => {
        const vendorCounts: Record<string, number> = {};
        for (const alert of integrationAlerts) {
            const vendor = alert.integration_type || 'Unknown';
            vendorCounts[vendor] = (vendorCounts[vendor] || 0) + 1;
        }

        const vendorIndex: Record<string, number> = {};
        return integrationAlerts.map(alert => {
            const vendor = alert.integration_type || 'Unknown';
            if (vendorCounts[vendor] > 1) {
                vendorIndex[vendor] = (vendorIndex[vendor] || 0) + 1;
                return `${vendor} (${vendorIndex[vendor]})`;
            }
            return vendor;
        });
    }, [integrationAlerts]);

    useEffect(() => {
        if (!selectedAlertId) return;
        if (alertCache[selectedAlertId]) return; // Already cached

        setIsLoading(true);
        AlertsService.getAlertById(selectedAlertId)
            .then((data: any) => {
                setAlertCache(prev => ({ ...prev, [selectedAlertId]: data }));
            })
            .catch((err: any) => {
                console.error('Failed to fetch alert:', err);
                setAlertCache(prev => ({ ...prev, [selectedAlertId]: { error: err.message } }));
            })
            .finally(() => setIsLoading(false));
    }, [selectedAlertId, alertCache]);

    const currentAlertData = selectedAlertId ? alertCache[selectedAlertId] : null;

    if (integrationAlerts.length === 0) return null;

    return (
        <Box>
            {/* Chip Switcher Row */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {integrationAlerts.map((alert, idx) => (
                    <Chip
                        key={alert.id}
                        label={chipLabels[idx]}
                        size="small"
                        variant={selectedAlertId === alert.id ? 'filled' : 'outlined'}
                        onClick={() => setSelectedAlertId(alert.id)}
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            ...(selectedAlertId === alert.id
                                ? {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                                }
                                : {
                                    borderColor: theme.palette.border.main,
                                    color: theme.palette.text.secondary,
                                    '&:hover': { backgroundColor: theme.palette.interactive.hover },
                                }
                            ),
                        }}
                    />
                ))}
            </Box>

            {/* Content Area */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: theme.palette.text.tertiary }} />
                </Box>
            ) : currentAlertData ? (
                <Box sx={{
                    borderRadius: 1,
                    overflow: 'hidden',
                    backgroundColor: theme.palette.background.inset,
                    '& pre': { margin: 0, fontSize: '0.75rem !important' },
                    '& pre::-webkit-scrollbar': { width: '5px', height: '5px' },
                    '& pre::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    '& pre::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.border.main, borderRadius: '3px' },
                    '& pre::-webkit-scrollbar-thumb:hover': { backgroundColor: theme.palette.border.strong },
                    '& pre::-webkit-scrollbar-corner': { backgroundColor: 'transparent' },
                }}>
                    <SyntaxHighlighter
                        language="json"
                        style={vscDarkPlus}
                        customStyle={{
                            backgroundColor: 'transparent',
                            padding: '1rem',
                            maxHeight: '400px',
                            overflow: 'auto',
                        }}
                    >
                        {JSON.stringify(currentAlertData, null, 2)}
                    </SyntaxHighlighter>
                </Box>
            ) : (
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    Select an alert to view raw data
                </Typography>
            )}
        </Box>
    );
};

// ============================================================================
// RAG-ROUTED INVESTIGATION COMPONENTS
// ============================================================================

const CONFIDENCE_THRESHOLD = 0.62;

const QualitySignalChip: React.FC<{ session: PlaybookSession }> = ({ session }) => {
    const theme = useTheme();
    const { quality_signal, retrieved_incident_count } = session;
    if (!quality_signal || quality_signal === 'pending') {
        return (
            <Tooltip title="AI analysis is being computed">
                <Chip
                    size="small"
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CircularProgress size={8} sx={{ color: 'inherit' }} />
                            <span>Pending</span>
                        </Box>
                    }
                    sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: theme.palette.text.disabled, backgroundColor: theme.palette.background.tertiary }}
                />
            </Tooltip>
        );
    }
    if (quality_signal === 'strong') {
        return (
            <Tooltip title="AI context is strong — this incident matched known patterns">
                <Chip size="small" label="● Strong" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: theme.palette.success.main, backgroundColor: colorUtils.withOpacity(theme.palette.success.main, 0.1) }} />
            </Tooltip>
        );
    }
    const n = retrieved_incident_count;
    return (
        <Tooltip title={`Context is limited${n ? ` — based on ${n} past case${n !== 1 ? 's' : ''}` : ''} — review recommendations carefully`}>
            <Chip size="small" label="● Limited" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: theme.palette.warning.main, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1) }} />
        </Tooltip>
    );
};

const PastIncidentsPanel: React.FC<{
    session: PlaybookSession;
    clientId: string;
    defaultExpanded?: boolean;
}> = ({ session, clientId, defaultExpanded = false }) => {
    const theme = useTheme();
    const [memories, setMemories] = useState<IncidentMemory[]>([]);
    const [loading, setLoading] = useState(false);

    const refs = session.retrieved_incident_ids;

    useEffect(() => {
        if (!refs?.length) return;
        setLoading(true);
        CuratorService.getMemories(refs.map(r => r.id), clientId)
            .then(setMemories)
            .catch(() => setMemories([]))
            .finally(() => setLoading(false));
    }, [refs, clientId]);

    if (!refs?.length) return null;

    return (
        <CollapsibleSection
            title={`SIMILAR PAST CASES (${refs.length})`}
            icon={<HistoryIcon fontSize="small" />}
            defaultExpanded={defaultExpanded}
        >
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={16} />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {memories.map((m) => {
                        const ref = refs.find(r => r.id === m.id);
                        const matchPct = ref ? Math.round(ref.reranker_score * 100) : null;
                        return (
                            <Box key={m.id} sx={{ p: 1.25, borderRadius: 1, backgroundColor: theme.palette.background.tertiary, border: `1px solid ${theme.palette.border.light}` }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                    {m.incident_type && (
                                        <Chip label={m.incident_type.toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }} />
                                    )}
                                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', ml: 'auto' }}>
                                        {getRelativeTimeString(m.created_at)}
                                    </Typography>
                                    {matchPct !== null && (
                                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                            {matchPct}% match
                                        </Typography>
                                    )}
                                </Box>
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', lineHeight: 1.4 }}>
                                    {m.content_text.length > 140 ? m.content_text.slice(0, 140) + '…' : m.content_text}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </CollapsibleSection>
    );
};

const RoughStepsPanel: React.FC<{
    session: PlaybookSession;
    clientId: string;
    onDone: () => void;
}> = ({ session, clientId, onDone }) => {
    const steps = session.ai_next_steps ?? [];
    const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
    const [decisions, setDecisions] = useState<Record<number, 'accepted' | 'skipped'>>({});
    const [saving, setSaving] = useState(false);

    if (!steps.length || session.analyst_verdict) return null;

    const actedCount = Object.keys(decisions).length;
    const allReviewed = actedCount >= steps.length;
    const acceptedCount = Object.values(decisions).filter(d => d === 'accepted').length;

    const decide = (idx: number, decision: 'accepted' | 'skipped') => {
        setDecisions(prev => ({ ...prev, [idx]: decision }));
        setExpandedIdx(null);
    };

    const handleDone = async () => {
        const verdict = acceptedCount === steps.length ? 'correct' : acceptedCount > 0 ? 'partial' : 'incorrect';
        setSaving(true);
        try {
            const items = Object.entries(decisions).map(([idx, decision]) => ({
                step_index: Number(idx),
                decision,
            }));
            await Promise.all([
                PlaybooksService.setSessionVerdict(session.id, verdict),
                PlaybooksService.setStepFeedback(session.id, items),
            ]);
            onDone();
        } catch {} finally { setSaving(false); }
    };

    const mono = { fontFamily: '"Courier New", Courier, monospace' } as const;

    return (
        <Box sx={{ mb: 2 }}>
            <PastIncidentsPanel session={session} clientId={clientId} defaultExpanded={false} />
            <Box sx={{ border: '1px solid #bbb', borderRadius: 0, backgroundColor: '#f5f5f5', mt: 1 }}>
                <Box sx={{ borderBottom: '1px solid #bbb', px: 1.5, py: 0.875, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ ...mono, fontSize: '0.68rem', fontWeight: 700, color: '#444', letterSpacing: '0.05em' }}>
                        SUGGESTED ACTIONS [UNVERIFIED]
                    </Typography>
                    <Typography sx={{ ...mono, fontSize: '0.65rem', color: '#888' }}>
                        {actedCount}/{steps.length} reviewed
                    </Typography>
                </Box>

                {steps.map((step, idx) => {
                    const decision = decisions[idx];
                    const isExpanded = expandedIdx === idx;
                    const preview = step.step.length > 60 ? step.step.slice(0, 60) + '…' : step.step;

                    return (
                        <Box key={idx} sx={{ borderBottom: idx < steps.length - 1 ? '1px solid #ddd' : 'none' }}>
                            <Box
                                onClick={() => !decision && setExpandedIdx(isExpanded ? null : idx)}
                                sx={{
                                    px: 1.5, py: 0.875,
                                    display: 'flex', alignItems: 'flex-start', gap: 1,
                                    cursor: decision ? 'default' : 'pointer',
                                    backgroundColor: decision === 'accepted' ? '#f0fff0' : 'transparent',
                                }}
                            >
                                <Typography component="span" sx={{ ...mono, fontSize: '0.7rem', color: '#666', mt: '2px', flexShrink: 0, userSelect: 'none', width: 10 }}>
                                    {decision === 'accepted' ? '✓' : decision === 'skipped' ? '—' : isExpanded ? 'v' : '>'}
                                </Typography>
                                <Typography sx={{
                                    ...mono, fontSize: '0.72rem',
                                    color: decision ? '#888' : '#222',
                                    lineHeight: 1.4, flex: 1,
                                    textDecoration: decision === 'skipped' ? 'line-through' : 'none',
                                }}>
                                    {(isExpanded || decision) ? step.step : preview}
                                </Typography>
                            </Box>

                            {isExpanded && !decision && (
                                <Box sx={{ px: 2.5, pb: 1.25, borderTop: '1px dashed #ccc' }}>
                                    {step.rationale && (
                                        <Typography sx={{ ...mono, fontSize: '0.67rem', color: '#666', lineHeight: 1.45, mt: 0.75, mb: 1, display: 'block', fontStyle: 'italic' }}>
                                            // {step.rationale}
                                        </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                                        <Box component="button" onClick={() => decide(idx, 'skipped')} sx={{ ...mono, fontSize: '0.68rem', px: 1, py: 0.375, border: '1px solid #aaa', backgroundColor: 'transparent', cursor: 'pointer', color: '#666', borderRadius: 0, '&:hover': { backgroundColor: '#eee' } }}>
                                            skip
                                        </Box>
                                        <Box component="button" onClick={() => decide(idx, 'accepted')} sx={{ ...mono, fontSize: '0.68rem', px: 1, py: 0.375, border: '1px solid #333', backgroundColor: '#333', color: '#fff', cursor: 'pointer', borderRadius: 0, '&:hover': { backgroundColor: '#111' } }}>
                                            accept
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    );
                })}

                {allReviewed && (
                    <Box sx={{ borderTop: '1px solid #bbb', px: 1.5, py: 0.875, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ ...mono, fontSize: '0.67rem', color: '#666' }}>
                            {acceptedCount}/{steps.length} accepted
                        </Typography>
                        <Box component="button" onClick={handleDone} disabled={saving} sx={{ ...mono, fontSize: '0.68rem', px: 1.5, py: 0.375, border: '1px solid #333', backgroundColor: '#333', color: '#fff', cursor: saving ? 'wait' : 'pointer', borderRadius: 0, '&:hover': { backgroundColor: '#111' }, '&:disabled': { opacity: 0.5 } }}>
                            {saving ? 'saving…' : 'done →'}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const LowConfidenceDataView: React.FC<{
    session: PlaybookSession;
    clientId: string;
    onDone: () => void;
}> = ({ session, clientId, onDone }) => {
    const theme = useTheme();
    const [steps, setSteps] = useState<string[]>(['']);
    const [saving, setSaving] = useState(false);

    if (session.analyst_verdict) return null;

    const updateStep = (idx: number, val: string) => setSteps(prev => prev.map((s, i) => i === idx ? val : s));
    const removeStep = (idx: number) => setSteps(prev => prev.filter((_, i) => i !== idx));
    const addStep = () => setSteps(prev => [...prev, '']);

    const handleSubmit = async () => {
        const filled = steps.filter(s => s.trim());
        if (!filled.length) return;
        const notes = 'Investigation steps:\n' + filled.map((s, i) => `${i + 1}. ${s.trim()}`).join('\n');
        setSaving(true);
        try {
            await PlaybooksService.setSessionVerdict(session.id, 'correct', notes);
            onDone();
        } catch {} finally { setSaving(false); }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 1 }}>
                <Box sx={{ color: theme.palette.ui.iconDefault, display: 'flex' }}>
                    <AssessmentIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle2" sx={{ flex: 1, color: theme.palette.text.secondary, fontWeight: 600 }}>
                    INVESTIGATION
                </Typography>
            </Box>

            <Box sx={{ pl: 4, pr: 1 }}>
                <Box sx={{ mb: 1.5, px: 1.25, py: 0.875, borderRadius: 1, backgroundColor: theme.palette.background.tertiary, borderLeft: `2px solid ${theme.palette.border.main}` }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.72rem', lineHeight: 1.45 }}>
                        AI context is limited for this incident. Enriched data is shown below — assess and document your steps.
                    </Typography>
                </Box>

                <PastIncidentsPanel session={session} clientId={clientId} defaultExpanded={true} />

                <Box sx={{ mt: 1.25 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem', letterSpacing: '0.08em', fontWeight: 600, display: 'block', mb: 0.875 }}>
                        INVESTIGATION STEPS
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
                        {steps.map((s, idx) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                                <Typography variant="caption" sx={{ minWidth: 18, height: 18, borderRadius: '50%', background: theme.palette.background.tertiary, border: `1px solid ${theme.palette.border.main}`, color: theme.palette.text.tertiary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, mt: 0.75, flexShrink: 0 }}>
                                    {idx + 1}
                                </Typography>
                                <TextField
                                    value={s}
                                    onChange={(e) => updateStep(idx, e.target.value)}
                                    placeholder="Document this step…"
                                    size="small"
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.light } } }}
                                />
                                {steps.length > 1 && (
                                    <IconButton size="small" onClick={() => removeStep(idx)} sx={{ mt: 0.5, color: theme.palette.text.disabled, '&:hover': { color: theme.palette.error.main }, flexShrink: 0 }}>
                                        <CloseIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.875 }}>
                        <Button size="small" onClick={addStep} sx={{ fontSize: '0.72rem', color: theme.palette.text.tertiary, px: 0, height: 28, textTransform: 'none', minWidth: 'auto' }}>
                            + Add step
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={saving || !steps.some(s => s.trim())}
                            onClick={handleSubmit}
                            sx={{ fontSize: '0.72rem', px: 1.5, height: 28, textTransform: 'none', ml: 'auto' }}
                        >
                            {saving ? <CircularProgress size={11} color="inherit" /> : 'Submit for review'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// Routes between high/low confidence paths. Only for IOC-based (non-template) sessions.
const AIInvestigationPanel: React.FC<{
    session: PlaybookSession;
    clientId: string;
    isResolved: boolean;
    onRefresh: () => void;
    forceCollapsed?: boolean;
}> = ({ session, clientId, isResolved, onRefresh, forceCollapsed }) => {
    const theme = useTheme();
    const { ai_confidence, quality_signal, ai_next_steps, analyst_verdict } = session;

    // Pending — AI still computing
    if (quality_signal === 'pending') {
        return (
            <Box sx={{ px: 1, py: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                    <QualitySignalChip session={session} />
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>
                        AI is analysing this incident…
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {[0.85, 0.55, 0.7].map((w, i) => (
                        <Skeleton key={i} variant="rectangular" sx={{ width: `${w * 100}%`, height: 12, borderRadius: 0.5, backgroundColor: theme.palette.background.tertiary }} />
                    ))}
                </Box>
            </Box>
        );
    }

    // Old sessions without a confidence score — render the original flat list
    if (ai_confidence === null) {
        if (!ai_next_steps?.length) return null;
        return (
            <CollapsibleSection
                title={`AI SUGGESTED NEXT STEPS (${ai_next_steps.length})`}
                icon={<AssessmentIcon fontSize="small" />}
                defaultExpanded={true}
                forceCollapsed={forceCollapsed}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {ai_next_steps.map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Typography variant="caption" sx={{ minWidth: 18, height: 18, borderRadius: '50%', background: theme.palette.primary.main, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, mt: 0.25, flexShrink: 0 }}>
                                {idx + 1}
                            </Typography>
                            <Box>
                                <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontSize: '0.78rem', fontWeight: 500, lineHeight: 1.4 }}>
                                    {item.step}
                                </Typography>
                                {item.rationale && (
                                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.7rem', lineHeight: 1.4 }}>
                                        {item.rationale}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </CollapsibleSection>
        );
    }

    const isHighConf = ai_confidence >= CONFIDENCE_THRESHOLD;

    // Once a senior has recorded a verdict, hide the investigation panel —
    // the verdict panel below shows the outcome instead.
    if (analyst_verdict && !isResolved) return null;

    return (
        <Box>
            {!analyst_verdict && (
                <Box sx={{ px: 1, pb: 0.5, display: 'flex', alignItems: 'center' }}>
                    <QualitySignalChip session={session} />
                </Box>
            )}
            {isHighConf
                ? <RoughStepsPanel session={session} clientId={clientId} onDone={onRefresh} />
                : <LowConfidenceDataView session={session} clientId={clientId} onDone={onRefresh} />
            }
        </Box>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface TicketDetailsProps {
    ticket: ExtendedTicket;
    integrationAlerts: IntegrationAlert[];
}

const TicketDetails: React.FC<TicketDetailsProps> = ({ ticket, integrationAlerts }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { comments, attachments, isDetailsLoading: detailsLoading, isLoading } = useSelector((state: RootState) => state.tickets);
    const [isFooterLoading, setIsFooterLoading] = useState(true);
    const [playbookSession, setPlaybookSession] = useState<PlaybookSession | null>(null);
    const [playbookRecs, setPlaybookRecs] = useState<import('../types').PlaybookRecommendation[]>([]);
    const [escalateReviewOpen, setEscalateReviewOpen] = useState(false);
    const [escalateReviewerId, setEscalateReviewerId] = useState('');
    const [escalateNote, setEscalateNote] = useState('');
    const [escalating, setEscalating] = useState(false);
    const users = useSelector((state: RootState) => state.users.users);

    const handleEscalateForReview = async () => {
        if (!playbookSession) return;
        setEscalating(true);
        try {
            await PlaybooksService.escalateSession(
                playbookSession.id,
                escalateReviewerId || undefined,
                escalateNote || undefined,
            );
            setEscalateReviewOpen(false);
            setEscalateReviewerId('');
            setEscalateNote('');
            refreshPlaybook();
        } catch {
            // silent — user can retry
        } finally {
            setEscalating(false);
        }
    };

    const refreshPlaybook = useCallback(() => {
        PlaybooksService.getTicketPlaybook(ticket.id)
            .then((data) => {
                setPlaybookSession(data.session ?? null);
                setPlaybookRecs(data.recommendations);
            })
            .catch(() => {
                setPlaybookSession(null);
                setPlaybookRecs([]);
            });
    }, [ticket.id]);

    useEffect(() => {
        const loadTicketData = async () => {
            setIsFooterLoading(true);
            try {
                await Promise.all([
                    dispatch(fetchTicketComments(ticket.id) as any),
                    dispatch(fetchTicketAttachments(ticket.id) as any),
                ]);
            } finally {
                setIsFooterLoading(false);
            }
        };
        loadTicketData();
    }, [dispatch, ticket.id]);

    useEffect(() => { refreshPlaybook(); }, [refreshPlaybook]);

    const statusUI = getStatusMapping(ticket.meta.status, theme);
    const riskUI = getRiskScoreStyles(ticket.alert.socmate_risk_score, theme);
    const severityUI = getSeverityStyles(ticket.alert.vendor_severity, theme);

    const isResolved = [TicketStatus.RESOLVED, TicketStatus.CLOSED, TicketStatus.FALSE_POSITIVE].includes(ticket.meta.status);

    const [tabValue, setTabValue] = useState(0);
    const [footerOpen, setFooterOpen] = useState(false);

    const [aiSuggestions, setAISuggestions] = useState<AISuggestion[]>([]);
    const [aiSuggestionsLoading, setAISuggestionsLoading] = useState(false);
    const [actioningId, setActioningId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');

    // Re-fetch attachments (and comments) every time the footer is opened
    // so the analyst always sees the latest uploads from clients or other analysts.
    useEffect(() => {
        if (!footerOpen) return;
        dispatch(fetchTicketAttachments(ticket.id) as any);
        dispatch(fetchTicketComments(ticket.id) as any);
    }, [footerOpen, dispatch, ticket.id]);

    useEffect(() => {
        setAISuggestionsLoading(true);
        TicketsService.getAISuggestions(ticket.id, 'pending')
            .then((res) => setAISuggestions(res.suggestions))
            .catch(() => setAISuggestions([]))
            .finally(() => setAISuggestionsLoading(false));
    }, [ticket.id]);

    const handleAcceptSuggestion = async (s: AISuggestion) => {
        setActioningId(s.id);
        try {
            await TicketsService.acceptAISuggestion(ticket.id, s.id);
            setAISuggestions((prev) => prev.filter((x) => x.id !== s.id));
        } finally {
            setActioningId(null);
        }
    };

    const handleRejectSuggestion = async (s: AISuggestion) => {
        setActioningId(s.id);
        try {
            await TicketsService.rejectAISuggestion(ticket.id, s.id);
            setAISuggestions((prev) => prev.filter((x) => x.id !== s.id));
        } finally {
            setActioningId(null);
        }
    };

    const handleStartEdit = (s: AISuggestion) => {
        const raw = s.content;
        setEditValue(typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2));
        setEditingId(s.id);
    };

    const handleConfirmEdit = async (s: AISuggestion) => {
        setActioningId(s.id);
        try {
            let value: any = editValue;
            try { value = JSON.parse(editValue); } catch { /* leave as string */ }
            await TicketsService.editAndAcceptAISuggestion(ticket.id, s.id, value);
            setAISuggestions((prev) => prev.filter((x) => x.id !== s.id));
            setEditingId(null);
        } finally {
            setActioningId(null);
        }
    };
    const [footerHeight, setFooterHeight] = useState(160);
    const footerResizing = useRef(false);
    const footerResizeStartY = useRef(0);
    const footerResizeStartH = useRef(0);

    const handleFooterResizeMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        footerResizing.current = true;
        footerResizeStartY.current = e.clientY;
        footerResizeStartH.current = footerHeight;
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';

        const handleMouseMove = (ev: MouseEvent) => {
            if (!footerResizing.current) return;
            const delta = footerResizeStartY.current - ev.clientY;
            setFooterHeight(Math.max(100, Math.min(600, footerResizeStartH.current + delta)));
        };
        const handleMouseUp = () => {
            footerResizing.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };
    const [commentText, setCommentText] = useState('');
    const [alertExplorerActive, setAlertExplorerActive] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [resolveMode, setResolveMode] = useState(false);
    const [nis2ModalOpen, setNis2ModalOpen] = useState(false);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
    const [escalateComment, setEscalateComment] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleSendComment = async () => {
        if (!commentText.trim()) return;
        try {
            await (dispatch(addTicketComment({ ticketId: ticket.id, content: commentText }) as any)).unwrap();
            setCommentText('');
        } catch {}
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            await (dispatch(addTicketAttachment({ ticketId: ticket.id, file }) as any)).unwrap();
        } catch {}
        e.target.value = '';
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const IdentityIcon = TicketMappers.getIOCIcon(IOCType.EMAIL);
    const AssetIcon = TicketMappers.getTimelineIcon(TimelineEventType.PROCESS);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: theme.palette.background.default }}>

            {/* ================================================================
                HEADER: Triage Summary
            ================================================================ */}
            <Box sx={{ px: 3, pt: 2, pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
                {/* Top row: ID, Status | Actions | Close */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    {/* Metadata side */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Chip label={statusUI.label}
                              size="small"
                              sx={getStatusChipSx(statusUI.color)} />
                        <Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />
                        <TicketIdChip ticketId={ticket.id} />
                    </Box>

                    {/* Triage actions + Edit + Close */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Report">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => setNis2ModalOpen(true)}
                                    sx={{
                                        p: 0.75,
                                        color: theme.palette.info.main,
                                        '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.info.main, 0.08) },
                                    }}
                                >
                                    <ArticleOutlinedIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Resolve">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={isLoading || [TicketStatus.RESOLVED, TicketStatus.CLOSED].includes(ticket.meta.status)}
                                    onClick={() => setResolveDialogOpen(true)}
                                    sx={{
                                        p: 0.75,
                                        color: theme.palette.severity.low,
                                        '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.severity.low, 0.08) },
                                        '&.Mui-disabled': { opacity: 0.35 },
                                    }}
                                >
                                    <CheckCircleIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Escalate">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={isLoading || [TicketStatus.ESCALATED, TicketStatus.RESOLVED, TicketStatus.CLOSED].includes(ticket.meta.status)}
                                    onClick={() => { setEscalateComment(''); setEscalateDialogOpen(true); }}
                                    sx={{
                                        p: 0.75,
                                        color: theme.palette.severity.critical,
                                        '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.severity.critical, 0.08) },
                                        '&.Mui-disabled': { opacity: 0.35 },
                                    }}
                                >
                                    <WarningIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
                        <IconButton
                            size="small"
                            onClick={() => setEditModalOpen(true)}
                            sx={{
                                p: 0.75,
                                color: theme.palette.text.tertiary,
                                '&:hover': { color: theme.palette.text.primary },
                            }}
                        >
                            <EditOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => dispatch(clearSelectedTicket())}
                            sx={{
                                p: 0.75,
                                color: theme.palette.text.tertiary,
                                '&:hover': { color: theme.palette.text.primary },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Title */}
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem', lineHeight: 1.4 }}>
                    {ticket.title}
                </Typography>

                {/* Metrics row */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 3,
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                }}>
                    {/* Risk Score */}
                    <Box sx={{ minWidth: 'fit-content' }}>
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                            letterSpacing: '0.08em',
                            display: 'block',
                            mb: 0.5,
                            whiteSpace: 'nowrap'
                        }}>
                            RISK SCORE
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            <Typography sx={{
                                color: riskUI.color,
                                fontWeight: 700,
                                fontSize: '1.4rem',
                                lineHeight: 1,
                                fontFamily: '"JetBrains Mono", monospace',
                            }}>
                                {ticket.alert.socmate_risk_score}
                            </Typography>
                            <Typography variant="caption" sx={{
                                color: riskUI.color,
                                fontWeight: 600,
                                fontSize: '0.6rem',
                                textTransform: 'uppercase'
                            }}>
                                {riskUI.label}
                            </Typography>
                        </Box>
                    </Box>

                     {/*Vendor Severity*/}
                    <MetricBox
                        label="VENDOR SEVERITY"
                        value={severityUI.label}
                        color={severityUI.color}
                        subLabel={ticket.meta.source_system}
                    />

                    {/* Alert Type */}
                    <MetricBox
                        label="ALERT TYPE"
                        value={ticket.alert.alert_type}
                        subLabel={ticket.meta.category}
                        noTruncate
                    />

                </Box>

                {/* Risk Score Reason (if different from vendor) */}
                {ticket.alert.risk_score_reason && (
                    <Typography variant="caption" sx={{
                        display: 'block',
                        mt: 1.5,
                        color: theme.palette.text.tertiary,
                        fontStyle: 'italic',
                    }}>
                        {ticket.alert.risk_score_reason}
                    </Typography>
                )}

                {/* Correlation banner */}
                {ticket.duplicate_alerts && ticket.duplicate_alerts.total_count > 1 && (
                    <Box sx={{
                        mt: 2,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.06),
                        border: `1px solid ${colorUtils.withOpacity(theme.palette.warning.main, 0.18)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}>
                        <LayersIcon sx={{ fontSize: 14, color: theme.palette.warning.main, flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: theme.palette.warning.main, lineHeight: 1.4 }}>
                            <strong>Cluster:</strong> {ticket.duplicate_alerts.total_count} events across {ticket.duplicate_alerts.unique_platforms.join(', ')}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* ================================================================
                BODY: Scrollable content
            ================================================================ */}
            <Box
                sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                boxSizing: 'border-box',
                p: 3,
                // Delicate scrollbar styling
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.border.main,
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: theme.palette.border.strong,
                },
            }}>

                {/* Flagged for human review banner */}
                {playbookSession?.requires_human_review && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.25,
                        px: 2,
                        py: 1.25,
                        mb: 2,
                        borderRadius: 1,
                        backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.12),
                        border: `1px solid ${colorUtils.withOpacity(theme.palette.warning.main, 0.5)}`,
                        borderLeft: `3px solid ${theme.palette.warning.main}`,
                    }}>
                        <FlagIcon sx={{ fontSize: 15, color: theme.palette.warning.main, flexShrink: 0, mt: 0.15 }} />
                        <Box>
                            <Typography variant="caption" sx={{ color: theme.palette.warning.main, fontWeight: 700, fontSize: '0.75rem', display: 'block' }}>
                                Needs Human Review
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem', lineHeight: 1.4 }}>
                                {playbookSession.hitl_reason ?? 'This session was flagged for human review.'}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Analyst Analysis — AI summary always shown when available */}
                {(ticket.description || playbookSession?.ai_summary) && (
                    <AnalystAnalysisSection
                        clientRecommendations={ticket.client_recommendations}
                        resolutionSummary={ticket.resolution_summary}
                        description={ticket.description}
                        aiSummary={playbookSession?.ai_summary ?? null}
                        ticketId={ticket.id}
                        onAiRefresh={refreshPlaybook}
                    />
                )}

                {/* Affected Entities */}
                {(ticket.affected_identity || ticket.affected_asset) && (
                    <CollapsibleSection
                        title="AFFECTED ENTITIES"
                        icon={<LayersIcon fontSize="small" />}
                        badge={[ticket.affected_identity, ticket.affected_asset].filter(Boolean).length}
                        forceCollapsed={alertExplorerActive}
                    >
                        {ticket.affected_identity && (
                            <EntityRow
                                label="IDENTITY"
                                icon={<IdentityIcon sx={{ fontSize: 18 }} />}
                                primaryValue={ticket.affected_identity.username}
                                details={[
                                    { label: 'Role', value: ticket.affected_identity.user_role || '-' },
                                    { label: 'Privilege', value: ticket.affected_identity.privilege_level },
                                    { label: 'Dept', value: ticket.affected_identity.department || '-' },
                                    ...(ticket.affected_identity.email ? [{ label: 'Email', value: ticket.affected_identity.email }] : []),
                                ]}
                                statusBadge={{
                                    label: ticket.affected_identity.account_status,
                                    color: ticket.affected_identity.account_status === 'active'
                                        ? theme.palette.severity.low
                                        : theme.palette.warning.main
                                }}
                            />
                        )}
                        {ticket.affected_asset && (
                            <EntityRow
                                label="ASSET"
                                icon={<AssetIcon sx={{ fontSize: 18 }} />}
                                primaryValue={ticket.affected_asset.hostname}
                                details={[
                                    { label: 'IP', value: ticket.affected_asset.ip_address },
                                    { label: 'OS', value: ticket.affected_asset.os || '-' },
                                    { label: 'Segment', value: ticket.affected_asset.network_segment || '-' },
                                ]}
                                statusBadge={{
                                    label: ticket.affected_asset.criticality,
                                    color: getPriorityColor(ticket.affected_asset.criticality, theme)
                                }}
                            />
                        )}
                    </CollapsibleSection>
                )}

                {/* MITRE ATT&CK Mapping */}
                {ticket.mitre_mappings && ticket.mitre_mappings.length > 0 && (
                    <CollapsibleSection
                        title="MITRE ATT&CK MAPPING"
                        icon={<ShieldIcon fontSize="small" />}
                        badge={ticket.mitre_mappings.length}
                        forceCollapsed={alertExplorerActive}
                    >
                        {ticket.mitre_mappings.map((mapping, idx) => (
                            <Box key={idx} sx={{
                                display: 'flex', alignItems: 'center', gap: 2, mb: 1.5,
                                p: 1.5, borderRadius: 1,
                                backgroundColor: theme.palette.background.tertiary,
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="caption" sx={{
                                            color: theme.palette.text.tertiary,
                                            fontSize: '0.65rem',
                                        }}>
                                            {mapping.tactic}
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                            fontFamily: '"JetBrains Mono", monospace',
                                            color: theme.palette.text.tertiary,
                                            fontSize: '0.65rem',
                                        }}>
                                            {mapping.technique_id}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                        {mapping.technique_name}
                                        {mapping.sub_technique_name && ` — ${mapping.sub_technique_name}`}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" sx={{
                                        color: mapping.confidence >= 80 ? theme.palette.severity.low : theme.palette.text.tertiary,
                                        fontWeight: 600,
                                    }}>
                                        {mapping.confidence}% confidence
                                    </Typography>
                                </Box>
                                {mapping.mitre_url && (
                                    <IconButton
                                        size="small"
                                        href={mapping.mitre_url}
                                        target="_blank"
                                        sx={{ color: theme.palette.ui.iconDefault }}
                                    >
                                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                    </CollapsibleSection>
                )}

                {/* Indicators of Compromise */}
                {ticket.iocs && ticket.iocs.length > 0 && (
                    <CollapsibleSection
                        title="INDICATORS OF COMPROMISE"
                        icon={<BugReportIcon fontSize="small" />}
                        badge={ticket.iocs.length}
                        forceCollapsed={alertExplorerActive}
                    >
                        {ticket.iocs.map(ioc => {
                            const Icon = getIOCIcon(ioc.type);
                            return (
                                <Box key={ioc.id} sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mb: 1, // Increased margin for multi-line clarity
                                    py: 1,
                                    px: 1.5,
                                    borderRadius: 1.5, // Slightly rounder for the "card" feel
                                    backgroundColor: theme.palette.background.tertiary,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.interactive.hover,
                                        '& .ioc-actions': { opacity: 1 },
                                    },
                                }}>
                                    {/* Icon stays centered with the two lines of text */}
                                    <Icon sx={{ color: theme.palette.ui.iconDefault, fontSize: 18 }} />

                                    {/* Main Content Area */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}> {/* minWidth: 0 is critical for text wrapping in flex */}
                                        <Typography variant="caption" sx={{
                                            color: theme.palette.text.tertiary,
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.05em',
                                            display: 'block',
                                            mb: 0.25
                                        }}>
                                            {ioc.type.replace('hash_', '').toUpperCase()}
                                        </Typography>

                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            fontFamily: '"JetBrains Mono", monospace',
                                            color: theme.palette.text.secondary,
                                            fontSize: '0.75rem',
                                            wordBreak: 'break-all', // This forces the hash to wrap to a new line if too long
                                            lineHeight: 1.4,
                                        }}>
                                            {ioc.value}
                                        </Typography>
                                    </Box>

                                    {/* Right Side: Status & Actions */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {ioc.is_malicious && (
                                            <Chip
                                                label="Malicious"
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: '0.6rem',
                                                    fontWeight: 700,
                                                    backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.08),
                                                    color: theme.palette.error.main,
                                                    border: `1px solid ${colorUtils.withOpacity(theme.palette.error.main, 0.19)}`
                                                }}
                                            />
                                        )}

                                        <Box className="ioc-actions" sx={{
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            display: 'flex',
                                            gap: 0.5,
                                            ml: 1
                                        }}>
                                            <CopyIconButton value={ioc.value} />
                                            <Tooltip title="Pivot Search">
                                                <IconButton size="small" sx={{ color: theme.palette.ui.iconDefault }}>
                                                    <SearchIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </CollapsibleSection>
                )}

                {/* Correlated Alerts */}
                {ticket.duplicate_alerts && (ticket.duplicate_alerts.duplicates.length > 0 || ticket.duplicate_alerts.lead_alert) && (
                    <CollapsibleSection
                        title="CORRELATED ALERTS"
                        icon={<LayersIcon fontSize="small" />}
                        badge={ticket.duplicate_alerts.total_count}
                        forceCollapsed={alertExplorerActive}
                    >
                        {[ticket.duplicate_alerts.lead_alert, ...ticket.duplicate_alerts.duplicates].map((alert, idx) => {
                            const severityColor = (() => {
                                switch (alert.severity?.toLowerCase()) {
                                    case 'critical': return theme.palette.severity.critical;
                                    case 'high': return theme.palette.severity.high;
                                    case 'medium': return theme.palette.severity.medium;
                                    default: return theme.palette.severity.low;
                                }
                            })();
                            return (
                                <Box
                                    key={alert.alert_id}
                                    onClick={() => {
                                        dispatch(fetchAlertById(alert.alert_id) as any);
                                        navigate('/alerts');
                                    }}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        mb: 0.5,
                                        py: 0.75,
                                        px: 1.5,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.background.tertiary,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        borderLeft: `2px solid ${colorUtils.withOpacity(severityColor, 0.5)}`,
                                        '&:hover': {
                                            backgroundColor: theme.palette.interactive.hover,
                                            borderLeftColor: severityColor,
                                        },
                                    }}
                                >
                                    {/* Severity dot */}
                                    <Box sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: severityColor,
                                        flexShrink: 0,
                                    }} />

                                    {/* Alert name + source */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            fontWeight: 600,
                                            color: theme.palette.text.primary,
                                            fontSize: '0.75rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {alert.alert_name}
                                            {idx === 0 && (
                                                <Box component="span" sx={{
                                                    ml: 1,
                                                    fontSize: '0.6rem',
                                                    fontWeight: 700,
                                                    color: theme.palette.text.tertiary,
                                                    letterSpacing: '0.05em',
                                                }}>
                                                    PRIMARY
                                                </Box>
                                            )}
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                            color: theme.palette.text.tertiary,
                                            fontSize: '0.65rem',
                                        }}>
                                            {alert.source_system}
                                            {alert.timestamp && ` · ${new Date(alert.timestamp).toLocaleString()}`}
                                        </Typography>
                                    </Box>

                                    {/* Severity chip */}
                                    <Chip
                                        label={alert.severity?.toUpperCase()}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            backgroundColor: colorUtils.withOpacity(severityColor, 0.08),
                                            color: severityColor,
                                            border: `1px solid ${colorUtils.withOpacity(severityColor, 0.2)}`,
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />

                                    {/* Arrow hint */}
                                    <OpenInNewIcon sx={{ fontSize: 12, color: theme.palette.text.tertiary, flexShrink: 0 }} />
                                </Box>
                            );
                        })}
                    </CollapsibleSection>
                )}

                {/* Network Context */}
                {ticket.network_context && (
                    <CollapsibleSection
                        title="NETWORK CONTEXT"
                        icon={<NetworkIcon fontSize="small" />}
                        defaultExpanded={true}
                        forceCollapsed={alertExplorerActive}
                    >
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mb: 1 }}>
                                    SOURCE
                                </Typography>
                                <CopyableValue value={`${ticket.network_context.source_ip}:${ticket.network_context.source_port}`} />
                                {ticket.network_context.source_geo && (
                                    <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.tertiary }}>
                                        {ticket.network_context.source_geo.city}, {ticket.network_context.source_geo.country}
                                        {ticket.network_context.source_geo.is_vpn && ' (VPN)'}
                                        {ticket.network_context.source_geo.is_tor && ' (TOR)'}
                                    </Typography>
                                )}
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mb: 1 }}>
                                    DESTINATION
                                </Typography>
                                <CopyableValue value={`${ticket.network_context.destination_ip}:${ticket.network_context.destination_port}`} />
                                {ticket.network_context.destination_geo && (
                                    <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.tertiary }}>
                                        {ticket.network_context.destination_geo.city}, {ticket.network_context.destination_geo.country}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                            <MetricBox label="PROTOCOL" value={ticket.network_context.protocol || '-'} />
                            <MetricBox label="DIRECTION" value={ticket.network_context.direction} />
                            {ticket.network_context.bytes_sent && (
                                <MetricBox label="BYTES SENT" value={ticket.network_context.bytes_sent.toLocaleString()} />
                            )}
                        </Box>
                    </CollapsibleSection>
                )}

                {/* Threat Intelligence */}
                {ticket.threat_intel && ticket.threat_intel.length > 0 && (
                    <CollapsibleSection
                        title="THREAT INTELLIGENCE"
                        icon={<LightbulbIcon fontSize="small" />}
                        badge={ticket.threat_intel.length}
                        forceCollapsed={alertExplorerActive}
                    >
                        {ticket.threat_intel.map((intel, idx) => (
                            <Box key={idx} sx={{
                                p: 2, mb: 1.5, borderRadius: 1,
                                backgroundColor: theme.palette.background.tertiary,
                            }}>
                                {(intel.threat_actor || intel.apt_group) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <ShieldIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                                            {intel.apt_group || intel.threat_actor}
                                        </Typography>
                                    </Box>
                                )}
                                {intel.campaign_association && (
                                    <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary, mb: 1 }}>
                                        Campaign: {intel.campaign_association}
                                    </Typography>
                                )}
                                {intel.reputation_score !== null && (
                                    <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.tertiary }}>
                                        Reputation: {intel.reputation_score}/100 via {intel.reputation_source}
                                    </Typography>
                                )}
                                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                                    Source: {intel.intel_source} • Confidence: {intel.confidence}%
                                </Typography>
                            </Box>
                        ))}
                    </CollapsibleSection>
                )}

                {/* Timeline */}
                {ticket.timeline && ticket.timeline.length > 0 && (
                    <CollapsibleSection
                        title="EVENT TIMELINE"
                        icon={<ScheduleIcon fontSize="small" />}
                        badge={ticket.timeline.length}
                        forceCollapsed={alertExplorerActive}
                    >
                        {ticket.timeline.map((event, idx) => {
                            const isFirst = idx === 0;
                            return (
                                <Box key={event.id || idx} sx={{
                                    display: 'flex', gap: 2, mb: 2,

                                    pl: 2.5, position: 'relative',
                                }}>
                                    <Box sx={{
                                        position: 'absolute', left: -4, top: 2,
                                        width: 7, height: 7, borderRadius: '50%',
                                        backgroundColor: event.is_key_event
                                            ? theme.palette.warning.main
                                            : isFirst
                                                ? theme.palette.primary.main
                                                : theme.palette.border.main,

                                    }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            fontFamily: '"JetBrains Mono", monospace',
                                            color: theme.palette.text.tertiary,
                                            fontSize: '0.7rem', mb: 0.5,
                                        }}>
                                            {formatDate(event.timestamp)}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            fontWeight: 600, color: theme.palette.text.primary,
                                            fontSize: '0.85rem', mb: 0.25,
                                        }}>
                                            {event.title}
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                            color: theme.palette.text.tertiary, lineHeight: 1.5,
                                        }}>
                                            {event.description}
                                        </Typography>
                                        {event.analyst_name && (
                                            <Typography variant="caption" sx={{
                                                display: 'block', mt: 0.5,
                                                color: theme.palette.text.brand,
                                                fontStyle: 'italic',
                                            }}>
                                                — {event.analyst_name}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </CollapsibleSection>
                )}

                {/* Alert Explorer */}
                {integrationAlerts.length > 0 && (
                    <CollapsibleSection
                        title="ALERT EXPLORER"
                        icon={<CodeIcon fontSize="small" />}
                        badge={integrationAlerts.length}
                        defaultExpanded={true}
                        onToggle={(expanded) => setAlertExplorerActive(expanded)}
                    >
                        <AlertExplorer integrationAlerts={integrationAlerts} />
                    </CollapsibleSection>
                )}

                {/* AI-routed investigation — confidence-based path selection */}
                {playbookSession && !playbookSession.incident_type && (
                    <AIInvestigationPanel
                        session={playbookSession}
                        clientId={playbookSession.client_id}
                        isResolved={isResolved}
                        onRefresh={refreshPlaybook}
                        forceCollapsed={alertExplorerActive}
                    />
                )}

                {/* Template-based Incident Response Procedure */}
                {playbookSession?.incident_type && (
                    <CollapsibleSection
                        title="INCIDENT RESPONSE PROCEDURE"
                        icon={<ShieldIcon fontSize="small" />}
                        defaultExpanded={true}
                        forceCollapsed={alertExplorerActive}
                    >
                        <DDoSPlaybookPanel
                            session={playbookSession}
                            onSessionRefresh={refreshPlaybook}
                        />
                    </CollapsibleSection>
                )}

                {/* IOC Decisions — analyst accept/skip suggested actions per IOC */}
                {playbookRecs.length > 0 && (
                    <CollapsibleSection
                        title="IOC DECISIONS"
                        icon={<BugReportIcon fontSize="small" />}
                        badge={playbookRecs.length}
                        forceCollapsed={alertExplorerActive}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {playbookRecs.map((rec) => (
                                <RecommendationCard key={rec.id} rec={rec} />
                            ))}
                        </Box>
                    </CollapsibleSection>
                )}

                {/* Impact & Remediation */}
                {ticket.impact_remediation && (
                    <CollapsibleSection
                        title="IMPACT & REMEDIATION"
                        icon={<AssessmentIcon fontSize="small" />}
                        defaultExpanded={true}
                        forceCollapsed={alertExplorerActive}
                    >
                        {/* Estimated Impact */}
                        {ticket.impact_remediation.estimated_business_impact && (
                            <Box sx={{
                                p: 2, mb: 2, borderRadius: 1,
                                backgroundColor: theme.palette.background.inset,
                                borderLeft: `3px  ${
                                    ticket.impact_remediation.impact_severity === 'critical'
                                        ? theme.palette.error.main
                                        : ticket.impact_remediation.impact_severity === 'high'
                                            ? theme.palette.warning.main
                                            : theme.palette.text.tertiary
                                }`,
                            }}>
                                <Typography variant="caption" sx={{
                                    color: theme.palette.text.tertiary,
                                    fontSize: '0.65rem', letterSpacing: '0.08em',
                                    display: 'block', mb: 0.5,
                                }}>
                                    ESTIMATED IMPACT
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                    {ticket.impact_remediation.estimated_business_impact}
                                </Typography>
                            </Box>
                        )}

                        {/* Recommended Actions */}
                        {ticket.impact_remediation.recommended_actions && ticket.impact_remediation.recommended_actions.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{
                                    color: theme.palette.text.tertiary,
                                    fontSize: '0.65rem', letterSpacing: '0.08em',
                                    display: 'block', mb: 1,
                                }}>
                                    REMEDIATION CHECKLIST
                                </Typography>
                                {ticket.impact_remediation.recommended_actions.map((action, idx) => (
                                    <Box key={action.id || idx} sx={{
                                        display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1,
                                    }}>
                                        {action.is_completed
                                            ? <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.severity.low, mt: 0.25 }} />
                                            : <UncheckedIcon sx={{ fontSize: 16, color: theme.palette.ui.iconDefault, mt: 0.25 }} />
                                        }
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{
                                                color: action.is_completed ? theme.palette.text.tertiary : theme.palette.text.primary,
                                                textDecoration: action.is_completed ? 'line-through' : 'none',
                                            }}>
                                                {action.action}
                                            </Typography>
                                            {action.priority === 'critical' && (
                                                <Chip
                                                    label="Critical"
                                                    size="small"
                                                    sx={{
                                                        height: 16, fontSize: '0.55rem', mt: 0.5,
                                                        backgroundColor: colorUtils.withOpacity(theme.palette.severity.critical, 0.08),
                                                        color: theme.palette.error.main,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </CollapsibleSection>
                )}

                {/* AI Suggestions */}
                {(aiSuggestionsLoading || aiSuggestions.length > 0) && (
                    <CollapsibleSection
                        title={aiSuggestionsLoading ? 'SUGGESTED STEPS' : `SUGGESTED STEPS (${aiSuggestions.length})`}
                        icon={<AISuggestionsIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />}
                        defaultExpanded={true}
                        forceCollapsed={alertExplorerActive}
                    >
                        {aiSuggestionsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={18} />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {aiSuggestions.map((s) => {
                                    const typeLabel: Record<string, string> = { mitre_tags: 'MITRE Tags', triage_verdict: 'Triage Verdict', root_cause: 'Root Cause', investigation_step: 'Investigation Step', similar_tickets: 'Similar Tickets' };
                                    const contentPreview = typeof s.content === 'string' ? s.content : JSON.stringify(s.content, null, 2);
                                    const isActioning = actioningId === s.id;
                                    return (
                                        <Box key={s.id} sx={{ p: 1.25, borderRadius: 1, backgroundColor: theme.palette.background.default, border: `1px solid ${theme.palette.border.light}` }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                                                        <Chip label={typeLabel[s.suggestion_type] ?? s.suggestion_type} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }} />
                                                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem' }}>{s.agent_name}</Typography>
                                                        {s.confidence_score != null && (
                                                            <Chip label={`${Math.round(s.confidence_score * 100)}% conf`} size="small" sx={{ height: 14, fontSize: '0.56rem', fontWeight: 600, color: theme.palette.text.tertiary, backgroundColor: theme.palette.background.tertiary }} />
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', fontFamily: s.suggestion_type === 'mitre_tags' ? 'monospace' : 'inherit', display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {contentPreview}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                    <Tooltip title="Accept as-is">
                                                        <IconButton size="small" disabled={isActioning} onClick={() => handleAcceptSuggestion(s)} sx={{ color: theme.palette.success.main, '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.success.main, 0.1) } }}>
                                                            {isActioning ? <CircularProgress size={12} /> : <AcceptIcon sx={{ fontSize: 16 }} />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit & Accept">
                                                        <IconButton size="small" disabled={isActioning} onClick={() => handleStartEdit(s)} sx={{ color: theme.palette.warning.main, '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1) } }}>
                                                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reject">
                                                        <IconButton size="small" disabled={isActioning} onClick={() => handleRejectSuggestion(s)} sx={{ color: theme.palette.error.main, '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.1) } }}>
                                                            <RejectIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </CollapsibleSection>
                )}


                {/* Verdict from a senior review, if one was recorded — not every ticket gets reviewed */}
                {playbookSession?.analyst_verdict && (
                    <Box sx={{ mb: 2 }}>
                        <VerdictPanel session={playbookSession} />
                    </Box>
                )}

                {/* Escalate for human review — always available to analysts as an action */}
                {playbookSession && !playbookSession.requires_human_review && (
                    <Box sx={{ mb: 2 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EscalateIcon sx={{ fontSize: 14 }} />}
                            onClick={() => {
                                dispatch(fetchAssignableUsers() as any);
                                setEscalateReviewOpen(true);
                            }}
                            sx={{
                                py: 0.75,
                                px: 2,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                color: theme.palette.severity?.critical ?? theme.palette.error.main,
                                borderColor: colorUtils.withOpacity(theme.palette.severity?.critical ?? theme.palette.error.main, 0.25),
                                '&:hover': {
                                    borderColor: theme.palette.error.main,
                                    backgroundColor: colorUtils.withOpacity(theme.palette.severity?.critical ?? theme.palette.error.main, 0.06),
                                },
                            }}
                        >
                            Escalate for Review
                        </Button>
                    </Box>
                )}

                {/* Escalate for Review dialog */}
                <Dialog open={escalateReviewOpen} onClose={() => setEscalateReviewOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ fontSize: '0.9rem', fontWeight: 700, pb: 1 }}>
                        Escalate for AI Review
                    </DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 2, fontSize: '0.75rem' }}>
                            This will flag the session for the review queue. Optionally assign it to a colleague.
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel sx={{ fontSize: '0.8rem' }}>Assign to (optional)</InputLabel>
                            <Select
                                value={escalateReviewerId}
                                label="Assign to (optional)"
                                onChange={(e) => setEscalateReviewerId(e.target.value)}
                                sx={{ fontSize: '0.8rem' }}
                            >
                                <MenuItem value=""><em>Unassigned</em></MenuItem>
                                {users.map((u) => (
                                    <MenuItem key={u.id} value={u.id} sx={{ fontSize: '0.8rem' }}>
                                        {u.first_name} {u.last_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            label="Note (optional)"
                            value={escalateNote}
                            onChange={(e) => setEscalateNote(e.target.value)}
                            InputProps={{ sx: { fontSize: '0.8rem' } }}
                            InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button size="small" onClick={() => setEscalateReviewOpen(false)} sx={{ fontSize: '0.75rem' }}>
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleEscalateForReview}
                            disabled={escalating}
                            startIcon={escalating ? <CircularProgress size={12} /> : <EscalateIcon sx={{ fontSize: 14 }} />}
                            sx={{ fontSize: '0.75rem' }}
                        >
                            {escalating ? 'Escalating…' : 'Escalate'}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>

            {/* ================================================================
                    FOOTER: Integrated Comments & Triage Actions
               ================================================================ */}
            <Box sx={{
                backgroundColor: theme.palette.background.paper,
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
            }}>
                {/* Drag handle */}
                <Box
                    onMouseDown={handleFooterResizeMouseDown}
                    sx={{
                        height: 4,
                        cursor: 'row-resize',
                        backgroundColor: 'transparent',
                        borderTop: `1px solid ${theme.palette.divider}`,
                        '&:hover': { backgroundColor: theme.palette.interactive.hover },
                        flexShrink: 0,
                    }}
                />

                {/* Collapsible header row */}
                <Box
                    onClick={() => setFooterOpen(o => !o)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: theme.palette.interactive.hover },
                    }}
                >
                    <Box sx={{ color: footerOpen ? theme.palette.ui.iconActive : theme.palette.ui.iconDefault, display: 'flex', mr: 1.5 }}>
                        {footerOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Box>
                    <Tabs
                        value={tabValue}
                        onChange={(_, v) => setTabValue(v)}
                        onClick={(e) => { e.stopPropagation(); setFooterOpen(true); }}
                        sx={{
                            minHeight: 28,
                            flex: 1,
                            '& .MuiTab-root': { py: 0.5, minHeight: 28, fontSize: '0.75rem' }
                        }}
                    >
                        <Tab label={isFooterLoading ? 'Comments (…)' : `Comments (${comments.length})`} />
                        <Tab label={isFooterLoading ? 'Attachments (…)' : `Attachments (${attachments.length})`} />
                    </Tabs>
                    {isFooterLoading && <CircularProgress size={12} sx={{ ml: 1, color: theme.palette.text.tertiary }} />}
                </Box>

                <Collapse in={footerOpen}>
                <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', height: footerHeight, overflow: 'hidden' }}>

                {/* Comments / Attachments list */}
                <Box sx={{ flex: 1, overflowY: 'auto', mb: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {!isFooterLoading && tabValue === 0 && (
                        comments.length === 0
                            ? <Typography variant="body2" sx={{ color: theme.palette.text.tertiary, textAlign: 'center', py: 2 }}>No comments yet</Typography>
                            : comments.map(comment => (
                                <Box key={comment.id} sx={{ display: 'flex', gap: 1.5, p: 1, borderRadius: 1, backgroundColor: theme.palette.background.default }}>
                                    <Avatar sx={{ width: 26, height: 26, fontSize: '0.65rem', bgcolor: theme.palette.primary.main, flexShrink: 0 }}>
                                        {comment.author ? `${comment.author.first_name[0]}${comment.author.last_name[0]}` : '?'}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                                {comment.author ? `${comment.author.first_name} ${comment.author.last_name}` : 'Unknown'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                                                {new Date(comment.created_at).toLocaleString()}
                                            </Typography>
                                            {comment.is_internal && (
                                                <Chip label="Internal" size="small" sx={{ height: 14, fontSize: '0.6rem' }} />
                                            )}
                                        </Box>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25, wordBreak: 'break-word' }}>
                                            {comment.content}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))
                    )}
                    {!isFooterLoading && tabValue === 1 && (
                        attachments.length === 0
                            ? <Typography variant="body2" sx={{ color: theme.palette.text.tertiary, textAlign: 'center', py: 2 }}>No attachments yet</Typography>
                            : attachments.map(attachment => (
                                <Box key={attachment.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 1, backgroundColor: theme.palette.background.default }}>
                                    <AttachFileIcon sx={{ fontSize: 16, color: theme.palette.text.tertiary, flexShrink: 0 }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.primary, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {attachment.filename}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                                            {formatFileSize(attachment.file_size)} · {attachment.file_type}
                                            {attachment.uploaded_by && ` · ${attachment.uploaded_by.first_name} ${attachment.uploaded_by.last_name}`}
                                            {` · ${new Date(attachment.created_at).toLocaleDateString()}`}
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Download">
                                        <IconButton
                                            size="small"
                                            sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.primary.main } }}
                                            onClick={async () => {
                                                const response = await api.get(`/tickets/${ticket.id}/attachments/${attachment.id}`, { responseType: 'blob' });
                                                const url = URL.createObjectURL(response.data);
                                                const a = document.createElement('a');
                                                a.href = url; a.download = attachment.filename; a.click();
                                                URL.revokeObjectURL(url);
                                            }}
                                        >
                                            <DownloadIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove">
                                        <IconButton
                                            size="small"
                                            sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.error.main } }}
                                            onClick={() => dispatch(deleteTicketAttachment({ ticketId: ticket.id, attachmentId: attachment.id }) as any)}
                                        >
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ))
                    )}
                </Box>

                {/* Input area */}
                {tabValue === 0 ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            fullWidth
                            placeholder="Write a finding..."
                            variant="outlined"
                            size="small"
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendComment(); } }}
                            autoComplete="off"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: theme.palette.background.default,
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme.palette.text.primary,
                                        borderWidth: '1px'
                                    },
                                },
                            }}
                        />
                        <IconButton
                            disabled={!commentText.trim()}
                            onClick={handleSendComment}
                            sx={{
                                bgcolor: theme.palette.background.tertiary,
                                borderRadius: 1,
                                height: 40,
                                width: 40
                            }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <Box>
                        <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} />
                        <Button
                            startIcon={<AttachFileIcon />}
                            variant="outlined"
                            size="small"
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                textTransform: 'none',
                                borderColor: theme.palette.border.main,
                                color: theme.palette.text.secondary,
                                '&:hover': { borderColor: theme.palette.text.primary },
                            }}
                        >
                            Upload Attachment
                        </Button>
                    </Box>
                )}
                </Box>
                </Collapse>
            </Box>


            {/* Edit Ticket Modal */}
            <EditTicketModal
                open={editModalOpen}
                onClose={() => { setEditModalOpen(false); setResolveMode(false); }}
                ticket={ticket}
                integrationAlerts={integrationAlerts}
                resolveMode={resolveMode}
            />

            <Nis2ReportModal
                open={nis2ModalOpen}
                onClose={() => setNis2ModalOpen(false)}
                ticket={ticket}
            />

            {/* Escalate Dialog with Comment */}
            <Dialog
                open={escalateDialogOpen}
                onClose={() => setEscalateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                disableScrollLock
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.main}`,
                        borderRadius: 2,
                        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
                    },
                }}
            >
                <DialogContent sx={{ py: 3 }}>
                    <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                        <WarningIcon sx={{ fontSize: 40, color: theme.palette.severity.critical, mb: 1 }} />
                        <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                            Escalate this ticket?
                        </Typography>
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Escalation reason or notes..."
                        value={escalateComment}
                        onChange={(e) => setEscalateComment(e.target.value)}
                        disabled={isLoading}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme.palette.background.default,
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.text.primary,
                                    borderWidth: '1px',
                                },
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setEscalateDialogOpen(false)}
                        disabled={isLoading}
                        sx={{ color: theme.palette.text.secondary, textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={isLoading}
                        onClick={async () => {
                            try {
                                await (dispatch(escalateTicket({
                                    ticketId: ticket.id,
                                    comment: escalateComment.trim() || undefined,
                                }) as any)).unwrap();
                                setEscalateDialogOpen(false);
                            } catch {}
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {isLoading ? <CircularProgress size={20} /> : 'Escalate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── AI Suggestion Edit Modal ─────────────────────────────── */}
            {(() => {
                const s = editingId ? aiSuggestions.find((x) => x.id === editingId) ?? null : null;
                const typeLabel: Record<string, string> = { mitre_tags: 'MITRE Tags', triage_verdict: 'Triage Verdict', root_cause: 'Root Cause', investigation_step: 'Investigation Step', similar_tickets: 'Similar Tickets' };
                const isActioning = actioningId === editingId;
                const originalPreview = s ? (typeof s.content === 'string' ? s.content : JSON.stringify(s.content, null, 2)) : '';
                return (
                    <Dialog
                        open={!!editingId}
                        onClose={() => setEditingId(null)}
                        maxWidth="sm"
                        fullWidth
                        disableScrollLock
                        PaperProps={{
                            sx: {
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.border.main}`,
                                borderRadius: 2,
                                boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
                            },
                        }}
                    >
                        <DialogTitle sx={{ pb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EditOutlinedIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: theme.palette.text.primary }}>
                                    Edit AI Suggestion
                                </Typography>
                                {s && (
                                    <Chip
                                        label={typeLabel[s.suggestion_type] ?? s.suggestion_type}
                                        size="small"
                                        sx={{ ml: 0.5, height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                                    />
                                )}
                            </Box>
                            {s && (
                                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mt: 0.5 }}>
                                    {s.agent_name}{s.confidence_score != null ? ` · ${Math.round(s.confidence_score * 100)}% confidence` : ''}
                                </Typography>
                            )}
                        </DialogTitle>
                        <DialogContent sx={{ pt: 0 }}>
                            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
                                Original
                            </Typography>
                            <Box sx={{ p: 1.5, mb: 2.5, borderRadius: 1, backgroundColor: theme.palette.background.default, border: `1px solid ${theme.palette.border.light}` }}>
                                <Typography sx={{ fontSize: '0.85rem', color: theme.palette.text.secondary, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: s?.suggestion_type === 'mitre_tags' ? 'monospace' : 'inherit' }}>
                                    {originalPreview}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
                                Your edit
                            </Typography>
                            <TextField
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                multiline
                                minRows={4}
                                maxRows={12}
                                fullWidth
                                autoFocus
                                placeholder="Edit the suggestion content…"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '0.9rem',
                                        fontFamily: s?.suggestion_type === 'mitre_tags' ? 'monospace' : 'inherit',
                                        backgroundColor: theme.palette.background.default,
                                        '& fieldset': { borderColor: theme.palette.border.main },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.warning.main, borderWidth: '1px' },
                                    },
                                }}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                            <Button
                                onClick={() => setEditingId(null)}
                                disabled={isActioning}
                                sx={{ color: theme.palette.text.secondary, textTransform: 'none', fontWeight: 500 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                disabled={isActioning || !editValue.trim()}
                                onClick={() => s && handleConfirmEdit(s)}
                                startIcon={isActioning ? <CircularProgress size={14} color="inherit" /> : <AcceptIcon sx={{ fontSize: 16 }} />}
                                sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: theme.palette.warning.main, '&:hover': { backgroundColor: theme.palette.warning.dark } }}
                            >
                                {isActioning ? 'Accepting…' : 'Accept edited'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                );
            })()}
        </Box>
    );
};

export default TicketDetails;
